import React, {useMemo, useRef, useState} from 'react'
import {Link} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {isMobile} from "react-device-detect";

import {ucraftStore} from "../../store/ucraftStore";
import {departmentStore} from "../../store/departmentStore";
import {notificationStore} from "../../store/notificationStore";
import {authStore} from '../../store/authStore';
import {dashboardStore} from '../../store/dashboardStore';
import {buyCryptoStore} from "../../store/buyCryptoStore";
import {unityStore} from "../../store/unityStore";

import apiService from "../../services/apiServices";
import {cacheService} from "../../services/cacheService";

import {isValidEmail, isValidPass} from "../../utils";

import {tr} from "../../helpers/translations/tr";
import {useNavigateTo} from "../../helpers/navigateTo";
import {clearCache} from "../../helpers/clearCache";

import SigninLayout from "./SigninLayout";
import CheckboxComponent from "../Checkbox";
import CustomInput from "../CustomInput";
import CustomButton from "../CustomButton";
import LoginWithGoogle from "./LoginWith/Google";
import LoginWithApple from "./LoginWith/Apple";
import LoginWithFastex from "./LoginWith/Fastex";

import style from "./signin.module.css";
import scene from "../assets/guest.glb";

const SignIn = observer(() => {
    const {setIsConnectedPopup, setIsConnected} = ucraftStore;
    const {setUserType, setSelectedUserType} = departmentStore;
    const {setUserGuest} = apiService;
    const {setNotification} = notificationStore;
    const {setMyEventsActive} = dashboardStore;
    const {
        setNickname,
        setEmail,
        setHooryValue,
        setProvider,
        setAvatarImage,
        setShowUserDataPopup,
        setGender,
        setBirthDate
    } = authStore;
    const {setUserBalance} = buyCryptoStore;
    const {setAvatarUrl} = unityStore;

    const passRef = useRef();

    const {navigateTo} = useNavigateTo();

    const [isValid, setIsValid] = useState(true);
    const [emailValue, setEmailValue] = useState("");
    const [password, setPassword] = useState("");
    const [active, setActive] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);

    const getUserHomeSpace = (token) => apiService.getUserHomeSpace({token});

    const setData = () => apiService.setUserLogin({
        email: emailValue, password,
    });

    const emailHandler = (value) => {
        setEmailError(false);
        setPasswordError(false);
        setEmailValue(value);
    }

    const passwordHandler = (value) => {
        setEmailError(false);
        setPasswordError(false);
        setPassword(value);
    }

    const handleGuestClick = async () => {
        try {
            const {data: {data: {token, guest_id, username}}} = await setUserGuest()
            setMyEventsActive('-fastex')
            const alternateGuest = cacheService.get('AlternateGuest')
            clearCache();
            cacheService.set('Authorization', `Token ${token}`)
            cacheService.set('UserType', 'guest')
            cacheService.set('UserName', username)
            cacheService.set('Id', guest_id)
            cacheService.set('AlternateGuest', alternateGuest)

            if (!!alternateGuest) {
                navigateTo({pathname: `/dashboard/${alternateGuest}`}, {replace: true})
                cacheService.set('AlternateGuest', '')
            } else {
                navigateTo({pathname: '/dashboard', search: '?%signIn'})
            }
        } catch (e) {
            console.log("ERROR: ", e.message);
        }
    };

    const loginTo = async () => {
        setIsValid(false);
        setEmailError(false);
        setPasswordError(false);
        if (isValidPass(password) && isValidEmail(emailValue)) {
            try {
                const {
                    data: {
                        data: {
                            token,
                            public_id,
                            is_fastex_staff_user,
                            is_superuser,
                            ucraft_token,
                            show_user_type,
                            user_type,
                            username,
                            email,
                            hoory_website_token,
                            auth_provider,
                            avatar_image_url,
                            avatar_url,
                            user_balance,
                            gender,
                            day_of_dirth
                        }
                    }
                } = await setData();
                setUserType(show_user_type);
                setSelectedUserType(user_type);
                setMyEventsActive("-fastex");
                const alternateGuest = cacheService.get("AlternateGuest");
                const invitedUserToken = cacheService.get("invitedUser");
                clearCache();
                cacheService.set("is_fastex_staff_user", is_fastex_staff_user);
                cacheService.set("isSuperuser", is_superuser);
                cacheService.set("Authorization", `Token ${token}`);
                cacheService.set("Id", public_id);
                cacheService.set("UserType", "user");
                setUserBalance(user_balance)
                setNickname(username);
                setEmail(email);
                setGender(gender);
                setBirthDate(day_of_dirth);
                setHooryValue(hoory_website_token);
                setAvatarImage(avatar_image_url.replace("camera=fullbody", "camera=portrait&size=80"));
                setAvatarUrl(avatar_url || scene)
                setProvider(auth_provider)
                if (!gender && !cacheService.isGuest) setShowUserDataPopup(true);

                if (!!ucraft_token && !isMobile) {
                    cacheService.set("ac_token", ucraft_token);

                    setTimeout(() => {
                        setIsConnected(true);
                        setIsConnectedPopup(true);
                    }, 1000);
                }
                // if (active) {
                //     cacheService.set("email", emailValue);
                //     cacheService.set("password", btoa(password));
                //     cacheService.set("remember_data", true);
                // }
                if (!!invitedUserToken) {
                    const {data: {data: {room_public_id}}} = await getUserHomeSpace(invitedUserToken)
                    cacheService.remove(["invitedUser", "AlternateGuest"])
                    return navigateTo(`/dashboard/${room_public_id}`, {replace: true});
                }
                if (!!alternateGuest) {
                    if (!gender && !cacheService.isGuest) setShowUserDataPopup(true);
                    cacheService.set("AlternateGuest", "");
                    navigateTo({pathname: `/dashboard/${alternateGuest}`}, {replace: true});
                } else {
                    navigateTo({pathname: "/dashboard", search: "?%signIn"}, {replace: true});
                }
            } catch (e) {
                if (e.errors?.translation_key !== "email_not_confirm") {
                    setNotification({type: 'error', text: tr('wrong_email_or_password')})
                    setPasswordError(true)
                    setEmailError(true)
                } else {
                    setEmail(emailValue)
                }
            }
        } else {
            setNotification({type: 'error', text: tr("wrong_email_or_password")});
            setEmailError(true);
            setPasswordError(true);
        }
        setIsValid(true);
    }

    const disableSignInBtn = useMemo(() => {
        return !isValid || !emailValue || !password || emailError || passwordError;
    }, [isValid, emailValue, password, emailError, passwordError])


    return (
        <SigninLayout>
            <h1 className={style.auth_title}>{tr("glad_to_see_you")}</h1>
            <p className={style.auth_desc}>{tr("login_credentials")}</p>
            <div className={style.social_btns}>
                <LoginWithGoogle/>
                {navigator.userAgent.indexOf('Mac OS X') !== -1 && <LoginWithApple/>}
                <LoginWithFastex/>
            </div>
            <p className={style.or}>{tr("or")}</p>
            <div className={style.form_container} autoComplete={"off"}>
                <CustomInput
                    type="email"
                    value={emailValue}
                    setValue={emailHandler}
                    placeholder={tr("email")}
                    title={tr("enter_your_email")}
                    className={`${style.form_label} ${style.form_email} ${passwordError ? style.error : ""}`}
                    error={emailError}
                    autoComplete={"on"}
                    goToNextAction={() => passRef.current.focus()}
                    incomingId={"email"}
                />
                <CustomInput
                    ref={passRef}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    setValue={passwordHandler}
                    placeholder={tr("password")}
                    title={tr("enter_pass")}
                    className={`${style.form_label} ${passwordError ? style.error : ""}`}
                    isPassword={true}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    error={passwordError}
                    autoComplete={"on"}
                    goToNextAction={loginTo}
                    incomingId={"pass"}
                />
                <div className={style.forgot_pass}>
                    <Link className={style.forgot_pass_link} to={"/forgot"}>{tr("forgot_pass")}</Link>
                </div>
                <div className={style.remember_container} onClick={() => setActive(prev => !prev)}>
                    <CheckboxComponent checked={active}/>
                    <span className={style.remember_text}>{tr("remember_me")}</span>
                </div>
                <div className={style.do_you_have_account}>
                    <p>{tr("dont_have_an_account")}
                        <Link className={style.create_account}
                              to={"/signup"}>{tr("create")}</Link>
                    </p>
                </div>
                <CustomButton
                    action={loginTo}
                    disabled={disableSignInBtn}
                    isValid={isValid}
                >
                    {tr("sign_in")}
                </CustomButton>
                <span className={style.or}>{tr("or")}</span>
                <CustomButton
                    variant='white'
                    action={handleGuestClick}
                >
                    {tr("explore")}
                </CustomButton>
            </div>
        </SigninLayout>
    );
});

export default SignIn;